import CircularLoading from 'components/base/loading/CircularLoading';
import PageTitle from 'components/base/title/PageTitle';
import MuiButton from 'components/common/button/MuiButton';
import DateConverter from 'components/common/convert/DateConverter';
import ListEmpty from 'components/common/list/ListEmpty';
import AppModal from 'components/common/modal/AppModal';
import PlainModal from 'components/common/modal/PlainModal';
import CustomSpan from 'components/common/text/CustomSpan';
import T from 'components/common/text/T';
import PatientSearchFilter from 'components/patient/PatientSearchFilter';
import useCheckSetInput from 'lib/hooks/useCheckSetInput';
import useDateInput from 'lib/hooks/useDateInput';
import useInput from 'lib/hooks/useInput';
import { calculateAge, validInputString } from 'lib/library';
import { genderList, projectProcessFlagList } from 'lib/mapper';
import { OffsetPagingData } from 'lib/types/sharedTypes';
import { useDidUpdateEffect } from 'lib/utils';
import { debounce } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useHistory } from 'react-router-dom';
import { PatientActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import styled from 'styled-components';
import PatientModalContainer from './PatientModalContainer';
import { IPatient } from 'lib/types/patientTypes';

function PatientListContainer() {
  const {
    language,
    fetchPatientList: { list: patients, pagingData: patientsPagingData },
    fetchPatientListSuccess,
    fetchPatientListFailure,
    fetchPatientListPending,
  }: {
    language: string;
    fetchPatientList: {
      list: IPatient[];
      pagingData: OffsetPagingData;
    };
    fetchPatientListSuccess: boolean;
    fetchPatientListFailure: boolean;
    fetchPatientListPending: boolean;
  } = useShallowAppSelector((state: any) => ({
    language: state.base.language,
    fetchPatientList: state.patient.patientList.data || { list: {}, pagingData: {} },
    fetchPatientListSuccess: state.patient.patientList.success,
    fetchPatientListFailure: state.patient.patientList.failure,
    fetchPatientListPending: state.patient.patientList.pending,
  }));
  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);
  const [isLoadFailure, setIsLoadFailure] = useState<boolean>(false);
  const history = useHistory();
  const [patientList, setPatientList] = useState<IPatient[]>([]);
  const setMainPartnersValue = useMemo(() => {
    return fetchPatientListSuccess && setPatientList(draft => draft.concat(patients));
  }, [!!fetchPatientListSuccess]);
  const hasMorePatients = patientsPagingData?.page < patientsPagingData?.totalPage;

  const page = useInput(1);
  const period = useDateInput(null);
  const periodRange = useDateInput(null);
  const checkedProjectProcess = useCheckSetInput(new Set([1, 2, 3, 5, 6, 7, -3]));
  const keyword = useInput('');
  const lastNameInitial = useInput('');
  const isPms = useInput(0);

  const [type, setType] = useState<string>('new');

  const { ipcRenderer } = window;
  const { t } = useTranslation();

  let searchParams = useMemo(() => {
    let checkText = validInputString(keyword.value);
    keyword.setValue(checkText);
    return {
      page: page.value,
      keyword: checkText,
      stages: checkedProjectProcess.value.size ? [...checkedProjectProcess.value].join('%') : '',
      lastNameInitial: lastNameInitial.value,
      startDate: period.value ? period.value[0].unix() : '',
      endDate: period.value ? period.value[1].unix() : '',
      pageRange: 100,
      isPms: isPms.value,
    };
  }, [
    page.value,
    keyword.value,
    period.value,
    checkedProjectProcess.value,
    lastNameInitial.value,
    isPms.value,
  ]);

  const onFetchPatientList = debounce(() => {
    const { page } = patientsPagingData;
    if (!hasMorePatients) return;
    PatientActions.fetch_patient_list_request({ ...searchParams, page: page + 1 });
  }, 500);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleClickPatient = (ptCode: string) => {
    history.push(`/patient/detail/${ptCode}`);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    handleSearch();
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleSearch = () => {
    const checkText = validInputString(keyword.value);
    keyword.setValue(checkText);
    setPatientList([]);
    setIsLoadFailure(false);
    setIsLoadSuccess(false);
    PatientActions.fetch_patient_list_request({
      ...searchParams,
      keyword: checkText,
      page: 1,
    });
    page.setValue(1);
  };

  useEffect(() => {
    setPatientList([]);
    PatientActions.fetch_patient_list_request(searchParams);
    return () => {
      setIsLoadSuccess(false);
      setIsLoadFailure(false);
    };
  }, []);

  const handleRefreshList = () => {
    PatientActions.fetch_patient_list_request(searchParams);
    setIsLoadSuccess(false);
    setIsLoadFailure(false);
  };
  useDidUpdateEffect(() => {
    if (!fetchPatientListSuccess) return;
    setIsLoadSuccess(true);
  }, [fetchPatientListSuccess]);

  useDidUpdateEffect(() => {
    if (!fetchPatientListFailure) return;
    setIsLoadFailure(true);
  }, [fetchPatientListFailure]);

  useDidUpdateEffect(() => {
    setPatientList([]);
    page.setValue(1);
    if (!checkedProjectProcess.value.size) return;
    setIsLoadFailure(false);
    setIsLoadSuccess(false);
    PatientActions.fetch_patient_list_request({ ...searchParams, page: 1 });
  }, [period.value, checkedProjectProcess.value, lastNameInitial.value, isPms.value]);

  useEffect(() => {
    console.log('HDS] patients _________ ', patients);
  }, [patients]);

  return (
    <StyledPatientList data-component-name="PatientList">
      {useMemo(() => {
        return (
          <PlainModal isOpen={isModalOpen} onClose={handleCloseModal} width={1000}>
            <AppModal
              title={'Patient'}
              content={<PatientModalContainer type={type} onCloseModal={handleCloseModal} />}
              contentCardStyle={{
                padding: '0px',
                backgroundColor: 'transparent',
              }}
              isCloseIcon={true}
              onClick={handleCloseModal}
              onCancel={handleCloseModal}
              hideButton={true}
            />
          </PlainModal>
        );
      }, [isModalOpen])}
      <PageTitle title={t('NAV_MENU_PATIENT')} className="patientList__title_box" />
      <div className="patientList__container">
        <section className="patientList__container_row newPatient">
          <h1 className="sr-only">patient filter</h1>

          <div className="pateint__filter_box">
            <PatientSearchFilter
              period={period}
              checkedProjectProcess={checkedProjectProcess}
              isPms={isPms}
              keyword={keyword}
              lastNameInitial={lastNameInitial}
              onSearch={handleSearch}
            />
          </div>
          <div className="patientList__btn_box">
            <MuiButton
              disableElevation
              variant="contained"
              color="primary"
              className="lg patientList__create_btn inset-shadow-default border-radius-round"
              onClick={() => {
                handleOpenModal();
              }}
            >
              <CustomSpan fontSize={18} fontWeight={400}>
                {'+ '}
                <T>GLOBAL_NEW_PATIENT</T>
              </CustomSpan>
            </MuiButton>
          </div>
        </section>
        <section className="patientList__container_row patientList">
          <h1 className="sr-only">patient list</h1>
          <div className="patientList__table">
            <div className="patientList__table_header">
              <div className="patientList__table_row">
                <div className="patientList__table_row_item chartNo">
                  <T>GLOBAL_CHARTNO</T>
                </div>
                <div className="patientList__table_row_item name">
                  <T>GLOBAL_NAME</T>
                </div>
                <div className="patientList__table_row_item project">
                  <T>GLOBAL_PROJECT_DATE</T>
                </div>
                <div className="patientList__table_row_item age">
                  <T>GLOBAL_AGE</T>
                </div>
                <div className="patientList__table_row_item gender">
                  <T>GLOBAL_GENDER</T>
                </div>
                <div className="patientList__table_row_item connect">
                  <T>PATIENT_CONNECT</T>
                </div>
                <div className="patientList__table_row_item status">
                  <T>GLOBAL_PROJECT_STATUS</T>
                </div>
              </div>
            </div>
            <div className="patientList__table_body_wrapper  box-shadow-default border-radius-default">
              <div className="patientList__table_body">
                {!patientList?.length && fetchPatientListPending ? (
                  <div className="infinite-scroll-loading">
                    <CircularLoading />
                  </div>
                ) : (
                  <InfiniteScroll
                    className="patientList__table_body_scroll infinite-scroll-box"
                    dataLength={patientList?.length}
                    next={onFetchPatientList}
                    hasMore={hasMorePatients}
                    loader={
                      <div className="infinite-scroll-loading">
                        <CircularLoading />
                      </div>
                    }
                    height={580}
                    scrollThreshold={0.95}
                  >
                    {!!isLoadSuccess &&
                      (patientList.length > 0 ? (
                        patientList.map((item, idx) => {
                          const krName = `${!!item.lastName ? item.lastName : ''} ${
                            !!item.firstName ? item.firstName : ''
                          } ${!!item.identification ? item.identification : ''}`;
                          const enName = `${!!item.firstName ? item.firstName : ''} ${
                            !!item.lastName ? item.lastName : ''
                          } ${!!item.identification ? item.identification : ''}`;
                          const patientName = language === 'en' ? enName : krName;
                          const patientAge = calculateAge(item.birthday);
                          const patientStage = !!item.projectStage ? item.projectStage : -3;
                          const stageStatus = projectProcessFlagList.find(
                            i => i.id === patientStage,
                          );

                          return (
                            <div
                              key={idx}
                              className="patientList__table_row"
                              onClick={() => {
                                handleClickPatient(item.patientcode);
                              }}
                            >
                              <div className="patientList__table_row_item chartNo">
                                {item.chartNumber}
                              </div>
                              <div className="patientList__table_row_item name">{patientName}</div>
                              <div className="patientList__table_row_item project">
                                {item?.projectNewDate ? (
                                  <DateConverter
                                    language={language}
                                    timestamp={item?.projectNewDate}
                                    format={language === 'en' ? 'MMM/DD/YYYY' : 'YYYY/MM/DD'}
                                  />
                                ) : (
                                  `-`
                                )}
                              </div>
                              <div className="patientList__table_row_item age">
                                {!!patientAge.age || patientAge.age === 0 ? patientAge.age : '-'}
                                {/* {patientAge.age} */}
                              </div>
                              <div className="patientList__table_row_item gender">
                                {genderList[item?.gender as keyof typeof genderList]}
                              </div>
                              <div className="patientList__table_row_item connect">
                                {!!item.pms ? 'C' : '-'}
                              </div>
                              <div className="patientList__table_row_item status">
                                <CustomSpan fontColor={stageStatus?.color}>
                                  {stageStatus?.name}
                                </CustomSpan>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <ListEmpty text="GLOBAL_EMPTY_LIST_PATIENT" />
                      ))}
                    {!!isLoadFailure && (
                      <ListEmpty
                        text="GLOBAL_LIST_ERROR"
                        isRefreshButton={true}
                        onRefresh={handleRefreshList}
                      />
                    )}
                  </InfiniteScroll>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </StyledPatientList>
  );
}

const StyledPatientList = styled.section<{}>`
  margin-bottom: 60px;
  .patientList__container {
    display: flex;
    flex-direction: column;
    row-gap: 20px;
    .patientList__container_row {
      &.newPatient {
        width: 100%;
        padding-top: 4px;
        position: relative;
        .patientList__btn_box {
          position: absolute;
          top: 0;
          right: 0;
          .patientList__create_btn {
            width: 392px;
            height: 54px;
            svg {
              margin-right: 5px;
            }
          }
        }
      }
      &.patientList {
        width: 100%;
        height: 630px;
        .patientList__table {
          width: 100%;
          .patientList__table_header {
            width: 100%;
            height: 50px;
            border-radius: 5px 5px 0 0;
            background-color: #25256f;
            .patientList__table_row {
              .patientList__table_row_item {
                font-size: 16px;
                font-weight: 500;
                color: #ffffff;
              }
            }
          }
          .patientList__table_body_wrapper {
            position: relative;
            height: 580px;
            background-color: #ffffff;
          }
          .patientList__table_body {
            position: relative;
            .patientList__table_body_scroll {
              height: 580px;
              overflow-y: overlay;

              .patientList__table_row {
                &:not(:last-child) {
                  border-bottom: 1px solid #edf4fb;
                }
                &:hover {
                  cursor: pointer;
                  background-color: #fafafa;
                }
              }
            }
          }
        }
      }
    }

    .patientList__table_row {
      width: 100%;
      height: 50px;
      display: flex;
      align-items: center;
      padding-left: 20px;
      .patientList__table_row_item {
        &.chartNo {
          width: 220px;
          padding-left: 30px;
        }
        &.name {
          width: 390px;
        }
        &.project {
          width: 260px;
        }
        &.age {
          width: 110px;
        }
        &.gender {
          width: 150px;
        }
        &.connect {
          width: 150px;
        }
        &.status {
          width: 320px;
        }
      }
    }
  }
`;

export default React.memo(PatientListContainer);
